import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Chip, Paper,
  Select, MenuItem, FormControl, InputLabel, IconButton,
  Tooltip, Collapse, Divider, LinearProgress,
} from '@mui/material';
import {
  Gavel as OperationalIcon,
  Cloud as AzureGovIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as DoneIcon,
  PlayCircle as ActiveIcon,
  Schedule as NewIcon,
  FolderOpen as InitiativeIcon,
} from '@mui/icons-material';
import { get } from '../../../services/apiService';

const PRIMARY  = '#0f1f3d';
const MUTED    = '#6d788c';
const CAT_PREFIX = 'compliance-category-';
const QUARTER_TAGS = new Set(['q3-2026', 'q4-2026', 'h1-2026', 'h2-2026']);

const PILLAR_META = {
  operationalCompliance: { label: 'Operational Compliance', icon: OperationalIcon, color: '#1565c0' },
  azureGovCompliance:    { label: 'Azure Gov Compliance',   icon: AzureGovIcon,    color: '#6a1b9a' },
};

const QUARTERS = [
  { key: 'all',     label: 'All' },
  { key: 'h1-2026', label: 'H1 2026', color: '#e65100' },
  { key: 'h2-2026', label: 'H2 2026', color: '#1565c0' },
  { key: 'q3-2026', label: 'Q3 2026', color: '#e65100' },
  { key: 'q4-2026', label: 'Q4 2026', color: '#6a1b9a' },
];

const DONE_STATES   = new Set(['Closed', 'Done', 'Completed']);
const ACTIVE_STATES = new Set(['Active', 'In Progress', 'Committed']);

function getStateInfo(state) {
  if (DONE_STATES.has(state))   return { label: state, color: '#2e7d32', bg: '#e8f5e9', icon: <DoneIcon  sx={{ fontSize: 12 }} /> };
  if (ACTIVE_STATES.has(state)) return { label: state, color: '#1565c0', bg: '#e3f2fd', icon: <ActiveIcon sx={{ fontSize: 12 }} /> };
  return                               { label: state || 'New', color: '#795548', bg: '#efebe9', icon: <NewIcon    sx={{ fontSize: 12 }} /> };
}

function crColor(v) {
  if (v >= 100) return '#2e7d32';
  if (v >= 67)  return '#43a047';
  if (v >= 33)  return '#fb8c00';
  return '#e53935';
}

function featureQuarterTags(tags) {
  return (tags || []).filter(t => QUARTER_TAGS.has(t.toLowerCase()));
}

function featureInitiative(tags, subCatMap) {
  for (const t of (tags || [])) {
    if (t.toLowerCase().startsWith(CAT_PREFIX)) {
      const alias = t.slice(CAT_PREFIX.length);
      return subCatMap[alias] || null;
    }
  }
  return null;
}

function QuarterChip({ label, color }) {
  return (
    <Chip label={label} size="small" sx={{
      height: 18, fontSize: '0.67rem', fontWeight: 700,
      bgcolor: `${color}18`, color, borderRadius: '5px',
      '& .MuiChip-label': { px: 0.75 },
    }} />
  );
}

const PRIORITY_META = {
  1: { label: 'P1', color: '#c62828', bg: '#ffebee' },
  2: { label: 'P2', color: '#e65100', bg: '#fff3e0' },
  3: { label: 'P3', color: '#f57f17', bg: '#fffde7' },
  4: { label: 'P4', color: '#546e7a', bg: '#eceff1' },
};

const PriorityBadge = ({ value }) => {
  const meta = PRIORITY_META[value];
  if (!meta) return null;
  return (
    <Tooltip title={`Priority ${value}`}>
      <Box sx={{
        px: 0.75, py: 0.15, borderRadius: '5px',
        bgcolor: meta.bg, display: 'inline-flex', alignItems: 'center', flexShrink: 0
      }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: meta.color, lineHeight: 1.4 }}>
          {meta.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

function ProgressBar({ value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(value, 100)}%`, height: '100%', bgcolor: color, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, minWidth: 30, textAlign: 'right' }}>{value}%</Typography>
    </Box>
  );
}

const FeatureRow = ({ feature, pillarColor, subCatMap }) => {
  const [open, setOpen] = useState(false);
  const si    = getStateInfo(feature.state);
  const cr    = feature.completionRate || 0;
  const qTags = featureQuarterTags(feature.tags);
  const init  = featureInitiative(feature.tags, subCatMap);
  const adoUrl = `https://dev.azure.com/_workitems/edit/${feature.id}`;

  return (
    <Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 140px 100px 80px 56px 28px',
        alignItems: 'center', gap: 1.5,
        px: 2, py: 1.1, cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.025)' },
      }} onClick={() => setOpen(p => !p)}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1a1a1a', lineHeight: 1.35 }} noWrap>
            {feature.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
            {qTags.map(t => {
              const q = QUARTERS.find(q => q.key === t.toLowerCase());
              return q ? <QuarterChip key={t} label={q.label} color={q.color} /> : null;
            })}
            {init && (
              <Chip label={init} size="small" sx={{ height: 16, fontSize: '0.62rem', bgcolor: `${pillarColor}14`, color: pillarColor, '& .MuiChip-label': { px: 0.75 } }} />
            )}
          </Box>
        </Box>
        <ProgressBar value={cr} color={crColor(cr)} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Box sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: si.bg, display: 'inline-flex', alignItems: 'center', gap: 0.4, width: 'fit-content' }}>
            <Box sx={{ color: si.color, display: 'flex' }}>{si.icon}</Box>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: si.color, lineHeight: 1 }}>{si.label}</Typography>
          </Box>
          <PriorityBadge value={feature.priority} />
        </Box>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#555', textAlign: 'center' }}>
          {feature.storyPoints || '—'}
          {feature.storyPoints > 0 && <Typography component="span" sx={{ fontSize: '0.62rem', color: MUTED }}> pts</Typography>}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: MUTED, textAlign: 'center' }}>
          {feature.backlogItemCount ?? '—'}
        </Typography>
        <Tooltip title="Open in ADO">
          <IconButton size="small" onClick={e => { e.stopPropagation(); window.open(adoUrl, '_blank'); }} sx={{ p: 0.25 }}>
            <OpenInNewIcon sx={{ fontSize: 14, color: MUTED }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 4, py: 0.75, bgcolor: 'rgba(0,0,0,0.015)', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <Typography variant="caption" color={MUTED}>Feature #{feature.id} · {feature.pillarLabel}</Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

const H22026DeliveryView = () => {
  const [features, setFeatures]     = useState([]);
  const [subCats, setSubCats]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [quarter, setQuarter]       = useState('all');
  const [pillar, setPillar]         = useState('all');
  const [priority, setPriority]     = useState('all');
  const [openPillars, setOpenPillars] = useState({ operationalCompliance: true, azureGovCompliance: true });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [delRes, catRes] = await Promise.all([
          get('/api/compliance-h2-2026/delivery'),
          get('/api/compliance-h2-2026/subcategories'),
        ]);
        if (delRes.success) setFeatures(delRes.data || []);
        if (catRes.success) setSubCats(catRes.data || []);
      } catch (e) {
        setError(e.message || 'Failed to load delivery data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const subCatMap = useMemo(() => {
    const m = {};
    subCats.forEach(sc => { m[sc.alias] = sc.name; });
    return m;
  }, [subCats]);

  const filtered = useMemo(() => {
    let list = features;
    if (pillar !== 'all') list = list.filter(f => f.pillarKey === pillar);
    if (quarter !== 'all') {
      list = list.filter(f => (f.tags || []).some(t => t.toLowerCase() === quarter));
    }
    if (priority !== 'all') list = list.filter(f => String(f.priority) === priority);
    return list;
  }, [features, pillar, quarter, priority]);

  const byPillar = useMemo(() => {
    const m = {};
    for (const f of filtered) {
      if (!m[f.pillarKey]) m[f.pillarKey] = [];
      m[f.pillarKey].push(f);
    }
    return m;
  }, [filtered]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box display="flex" gap={1.5} flexWrap="wrap" mb={2.5}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Quarter / Half</InputLabel>
          <Select value={quarter} label="Quarter / Half" onChange={e => setQuarter(e.target.value)}>
            {QUARTERS.map(q => <MenuItem key={q.key} value={q.key}>{q.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Pillar</InputLabel>
          <Select value={pillar} label="Pillar" onChange={e => setPillar(e.target.value)}>
            <MenuItem value="all">All Pillars</MenuItem>
            {Object.entries(PILLAR_META).map(([k, m]) => <MenuItem key={k} value={k}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={priority} label="Priority" onChange={e => setPriority(e.target.value)}>
            <MenuItem value="all"><em>All priorities</em></MenuItem>
            {[1, 2, 3, 4].map(p => (
              <MenuItem key={p} value={String(p)}>P{p}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {Object.entries(PILLAR_META).map(([pillarKey, meta]) => {
        const feats = byPillar[pillarKey];
        if (!feats || feats.length === 0) return null;
        const Icon = meta.icon;
        const done = feats.filter(f => DONE_STATES.has(f.state)).length;

        return (
          <Paper key={pillarKey} elevation={0} sx={{ mb: 3, border: '1.5px solid rgba(0,0,0,0.07)', borderRadius: '14px', overflow: 'hidden' }}>
            <Box
              onClick={() => setOpenPillars(p => ({ ...p, [pillarKey]: !p[pillarKey] }))}
              sx={{
                px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
                bgcolor: `${meta.color}0d`, borderBottom: '1px solid rgba(0,0,0,0.07)',
                cursor: 'pointer', '&:hover': { bgcolor: `${meta.color}12` },
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: `${meta.color}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 18, color: meta.color }} />
              </Box>
              <Typography fontWeight={650} color="#1a1a1a" sx={{ fontSize: '0.9rem', flex: 1 }}>{meta.label}</Typography>
              <Typography variant="caption" color={MUTED}>{done}/{feats.length} done</Typography>
              {openPillars[pillarKey] ? <ExpandLessIcon sx={{ color: MUTED }} /> : <ExpandMoreIcon sx={{ color: MUTED }} />}
            </Box>

            {/* Column headers */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 140px 100px 80px 56px 28px',
              gap: 1.5, px: 2, py: 0.75,
              borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fafbfc',
            }}>
              {['Feature', 'Progress', 'Status', 'SP', 'Items', ''].map((col, i) => (
                <Typography key={i} variant="caption" sx={{ fontWeight: 600, color: MUTED, fontSize: '0.7rem', textAlign: i >= 3 ? 'center' : 'left' }}>
                  {col}
                </Typography>
              ))}
            </Box>

            <Collapse in={openPillars[pillarKey]}>
              {feats.map(f => (
                <FeatureRow key={f.id} feature={f} pillarColor={meta.color} subCatMap={subCatMap} />
              ))}
            </Collapse>
          </Paper>
        );
      })}

      {filtered.length === 0 && (
        <Box textAlign="center" py={6}>
          <Typography color={MUTED}>No features match the selected filters.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default H22026DeliveryView;
